import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, Observable, startWith, map } from 'rxjs';

import { LegalEntity } from '../../objects/legal-entity';
import { LegalEntityService } from '../../service/entity-services/legal-entity.service';
import { CreateTypeContract } from '../create-type-contract';

// Angular Material Imports
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

@Component({
  selector: 'app-new-legal-entity',
  templateUrl: './new-legal-entity.component.html',
  styleUrls: ['./new-legal-entity.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatAutocompleteModule
  ]
})
export class NewLegalEntityComponent extends CreateTypeContract implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private router = inject(Router);

  // Form and state management
  form: FormGroup;
  private formSubscription!: Subscription;

  // State autocomplete
  states: string[] = [];
  filteredStates$!: Observable<string[]>;
  
  currentLegalEntity: LegalEntity = {};
  
  // UI state
  saving = false;
  editingLegalEntityMode = false;
  savedAsInactiveInDb = false;
  okToLeaveIntakeForm = false;
  
  // Validation
  submitted = false;

  constructor() {
    super();
    
    // Generate legal entity number with timestamp
    const datetimestamp = (new Date()).getTime().toString();
    
    // Initialize reactive form
    this.form = this.fb.group({
      legalEntityNum: [{ value: datetimestamp, disabled: true }],
      legalEntityName: ['', [Validators.required, Validators.minLength(2)]],
      legalEntityParent: [''],
      address: [''],
      city: [''],
      state: [''],
      zip: ['', [Validators.pattern(/^\d{5}(-\d{4})?$/)]],
      fein: ['', [Validators.pattern(/^\d{2}-?\d{7}$/)]],
      inactive: [false],
      comments: ['']
    });
  }

  async ngOnInit() {
    // Check if we're in edit mode (passed via router state)
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state || history.state;

    if (state?.legalEntity && state?.editMode) {
      this.editingLegalEntityMode = true;
      this.currentLegalEntity = state.legalEntity;
      this.populateFormWithLegalEntity(state.legalEntity);
    }

    // Subscribe to form value changes
    this.formSubscription = this.form.valueChanges.subscribe(changes => {
      // Update currentLegalEntity with form values (including disabled fields)
      this.currentLegalEntity = {
        ...this.currentLegalEntity,
        ...changes,
        legalEntityNum: this.form.get('legalEntityNum')?.value // Include disabled field
      };
    });

    // Load states for autocomplete
    this.commonSheetsService.getGeographicStates().subscribe(states => {
      this.states = states || [];
      this.setupStateAutocomplete();
    });
  }

  /**
   * Populate form fields with existing legal entity data for editing
   */
  private populateFormWithLegalEntity(legalEntity: LegalEntity) {
    this.form.patchValue({
      legalEntityName: legalEntity.legalEntityName || '',
      legalEntityParent: legalEntity.legalEntityParent || '',
      address: legalEntity.address || '',
      city: legalEntity.city || '',
      state: legalEntity.state || '',
      zip: legalEntity.zip || '',
      fein: legalEntity.fein || '',
      inactive: legalEntity.inactive || false,
      comments: legalEntity.comments || ''
    });

    // Update the legalEntityNum (disabled field) to use existing value
    this.form.get('legalEntityNum')?.setValue(legalEntity.legalEntityNum);
  }

  private setupStateAutocomplete() {
    const stateControl = this.form.get('state');
    if (stateControl) {
      this.filteredStates$ = stateControl.valueChanges.pipe(
        startWith(''),
        map(value => this.filterStates(value || ''))
      );
    }
  }

  private filterStates(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.states.filter(state =>
      state.toLowerCase().includes(filterValue)
    );
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  // Form validation methods
  isFormValid(): boolean {
    return this.form.valid && !this.saving;
  }

  getValidationMessage(): string {
    if (this.form.get('legalEntityName')?.hasError('required')) {
      return 'Legal Entity name is required';
    }
    if (this.form.get('legalEntityName')?.hasError('minlength')) {
      return 'Legal Entity name must be at least 2 characters';
    }
    if (this.form.get('zip')?.hasError('pattern')) {
      return 'Invalid ZIP code format (use 12345 or 12345-6789)';
    }
    if (this.form.get('fein')?.hasError('pattern')) {
      return 'Invalid FEIN format (use 12-3456789)';
    }
    return 'Please correct the errors above';
  }

  // Event handlers
  inactiveToggleChanged(event: any) {
    const isInactive = event.checked;
    this.form.patchValue({ inactive: isInactive });
  }

  // Helper to safely get inactive value as boolean
  get isInactive(): boolean {
    return this.form.get('inactive')?.value ?? false;
  }

  // Helper to check if entity is saved as inactive
  get isDisabledDueToInactive(): boolean {
    return this.savedAsInactiveInDb && (this.currentLegalEntity.inactive ?? false);
  }

  cancelSubmit() {
    this.okToLeaveIntakeForm = true;
    this.router.navigateByUrl('/securehome/legal-entities');
  }

  onSubmit() {
    this.submitted = true;
    
    if (!this.isFormValid()) {
      return;
    }

    this.saving = true;
    this.okToLeaveIntakeForm = true;

    // Get form value including disabled fields
    const legalEntityToSave: LegalEntity = {
      ...this.form.getRawValue() // getRawValue includes disabled fields
    };

    console.log('LEGAL ENTITY TO SAVE: ', legalEntityToSave);

    // Subscribe to the Observable returned by the service
    this.legalEntityService.writeLegalEntity(legalEntityToSave).subscribe({
      next: (response) => {
        console.log('Legal Entity saved successfully', response);
        this.router.navigateByUrl('/securehome/legal-entities');
      },
      error: (error) => {
        console.error('Error saving legal entity:', error);
        alert('Failed to save legal entity. Please try again.');
        this.saving = false; // Re-enable the save button on error
      }
    });
  }

  // Helper methods for display
  get isEditMode(): boolean {
    return this.editingLegalEntityMode;
  }

  get canSave(): boolean {
    return !this.isDisabledDueToInactive;
  }
}